import { z } from 'zod';

export const FREE_SUBSCRIPTION_ID = 'free';

// Card/Payment schemas and types
export const PaymentProviderSchema = z.enum(['stripe', 'paypal']);
export const CardTypeSchema = z.enum(['visa', 'mastercard', 'amex', 'discover', 'other', 'none']);

// Simplified Payment method schema - only includes minimal data for client
export const PaymentMethodSchema = z.object({
  id: z.string(), // Stripe payment method ID
  type: z.enum([
    'card',
    'us_bank_account',
    'paypal',
    'apple_pay',
    'google_pay',
    'link',
    'sepa_debit',
    'other',
  ]),
  displayName: z.string(), // Human-readable display name (e.g., "•••• 4242", "PayPal", etc.)
  isPrimary: z.boolean(),
});

export const PlanFeatureSchema = z.object({
  text: z.string(),
  included: z.boolean(),
});

export const SubscriptionPlanSchema = z.object({
  id: z.string(),
  name: z.string(),
  price: z.number(),
  interval: z.enum(['month', 'year']),
  description: z.string(),
  features: z.array(PlanFeatureSchema),
  isCurrent: z.boolean().optional(),
  isPopular: z.boolean().optional(),
  stripePriceId: z.string().optional(),
});

export const SubscriptionSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  planId: z.string(),
  status: z.string(),
  currentPeriodStart: z.date(),
  currentPeriodEnd: z.date(),
  paymentMethod: PaymentMethodSchema.optional(),
  quantity: z.number().optional(), // Number of seats/licenses in the subscription
  organizationId: z.string().optional(), // WorkOS organization ID that owns this subscription
});

export const UserBillingDataSchema = z.object({
  subscription: SubscriptionSchema.nullable(),
  plans: z.array(SubscriptionPlanSchema),
  success: z.boolean(),
  message: z.string().optional(),
  error: z.string().optional(),
});

export const UpdateSubscriptionRequestSchema = z.object({
  userId: z.string(),
  action: z.enum(['subscribe', 'upgrade', 'downgrade', 'cancel']),
  priceId: z.string().optional(),
  paymentMethodId: z.string().optional(),
});

export const UpdateSubscriptionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  error: z.string().optional(),
  subscription: SubscriptionSchema.optional(),
});

// Additional workflow-specific schemas
export const GetUserSubscriptionInputSchema = z.object({
  organizationId: z.string(), // Organization ID for customer lookup/creation (required)
  userId: z.string().optional(), // Optional userId for backward compatibility
  email: z.string().optional(), // Optional email for customer creation
});

export const GetUserSubscriptionOutputSchema = UserBillingDataSchema.extend({
  subscriptionError: z.string().nullable().optional(),
});

export const UpdateSubscriptionInputSchema = z.object({
  organizationId: z.string(), // Organization ID for customer lookup/creation (required)
  priceId: z.string().optional(), // Optional for cancellations
  paymentMethodId: z.string().optional(),
  action: z.enum(['subscribe', 'upgrade', 'downgrade', 'cancel']).optional().default('subscribe'),
});

export const UpdateSubscriptionOutputSchema = UpdateSubscriptionResponseSchema;

// Billing Intent Management Schemas
export const CreateBillingIntentInputSchema = z.object({
  organizationId: z.string(), // Organization ID for customer lookup/creation (required)
  userId: z.string().optional(), // Optional userId for backward compatibility
  email: z.string().optional(),
});

export const CreateBillingIntentOutputSchema = z.object({
  clientSecret: z.string(),
  customerId: z.string().optional(),
  success: z.boolean(),
  error: z.string().optional(),
});

// Customer Portal Session Schemas
export const CreatePortalSessionInputSchema = z.object({
  returnUrl: z.string().optional(),
});

export const CreatePortalSessionOutputSchema = z.object({
  url: z.string(),
  success: z.boolean(),
  error: z.string().optional(),
});

// Checkout Session Schemas
export const CreateCheckoutSessionInputSchema = z.object({
  // No fields required - organization ID is taken from the authenticated user
});

export const CreateCheckoutSessionOutputSchema = z.object({
  customerSessionClientSecret: z.string(),
  success: z.boolean(),
  error: z.string().optional(),
});

// Inferred types from schemas
export type PaymentProvider = z.infer<typeof PaymentProviderSchema>;
export type CardType = z.infer<typeof CardTypeSchema>;
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export type PlanFeature = z.infer<typeof PlanFeatureSchema>;
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>;
export type Subscription = z.infer<typeof SubscriptionSchema>;
export type UserBillingData = z.infer<typeof UserBillingDataSchema>;
export type UpdateSubscriptionRequest = z.infer<typeof UpdateSubscriptionRequestSchema>;
export type UpdateSubscriptionResponse = z.infer<typeof UpdateSubscriptionResponseSchema>;

// Workflow-specific inferred types
export type GetUserSubscriptionInput = z.infer<typeof GetUserSubscriptionInputSchema>;
export type GetUserSubscriptionOutput = z.infer<typeof GetUserSubscriptionOutputSchema>;
export type UpdateSubscriptionInput = z.infer<typeof UpdateSubscriptionInputSchema>;
export type UpdateSubscriptionOutput = z.infer<typeof UpdateSubscriptionOutputSchema>;

// Billing Intent workflow inferred types
export type CreateBillingIntentInput = z.infer<typeof CreateBillingIntentInputSchema>;
export type CreateBillingIntentOutput = z.infer<typeof CreateBillingIntentOutputSchema>;

// Checkout Session workflow inferred types
export type CreateCheckoutSessionInput = z.infer<typeof CreateCheckoutSessionInputSchema>;
export type CreateCheckoutSessionOutput = z.infer<typeof CreateCheckoutSessionOutputSchema>;

// Customer Portal Session workflow inferred types
export type CreatePortalSessionInput = z.infer<typeof CreatePortalSessionInputSchema>;
